import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { GuideButton } from "./workspace/GuideButton";
import { BarChart3, ClipboardCheck, FileText, LayoutDashboard, ShieldCheck, Sparkles, Users, Package, PackageCheck, AlertTriangle, CreditCard, Milestone, MessageSquare, Calculator, Ruler, FileWarning, RefreshCw, Wallet, ChevronDown } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { PhoneVerifyBanner } from "../components/PhoneVerifyBanner";
import { API, roleToSupplierId } from "./workspace/shared";
import { InboxBell } from "./workspace/InboxBell";
import { Overview, Jobs } from "./workspace/Overview";
import { Directory } from "./workspace/Directory";
import { DisputeModal, ResolveDisputeModal } from "./workspace/Disputes";
import { DesignProjects, DesignJobModal } from "./workspace/DesignProjects";
import { StudioProfile, DesignServices } from "./workspace/DesignServices";
import { DesignLibrary } from "./workspace/DesignLibrary";
import { RateDeliveryModal, SupplierAnalytics, PrinterCapabilitiesPanel } from "./workspace/SupplierAnalytics";
import { PlansPage } from "./workspace/PlansPage";
import BidCalculatorPage from "./workspace/BidCalculator";
import PrintTools from "./workspace/tools/PrintTools";
import { EwayBillQueue } from "./workspace/EwayBillQueue";
import { ZohoSyncReport } from "./workspace/ZohoSyncReport";
import { AdminSubRfqs } from "./workspace/AdminSubRfqs";
import { DeliveryRateCard } from "./workspace/DeliveryRateCard";
import { AdminDesign } from "./workspace/AdminDesign";
import { AdminSupplierNetwork } from "./workspace/AdminSupplierNetwork";
import { PartnerOverview, PartnerJobBoard, PartnerOrders, PartnerCapabilities } from "./workspace/PartnerWorkspace";
import { AdminControls } from "./workspace/AdminControls";
import { UserInvoices, SupplierSettlements } from "./workspace/Finance";
import { SupplierMilestones, PayoutsRouter } from "./workspace/SupplierPayouts";
import { JobDrawer } from "./workspace/JobDrawer";
import { JobModal, PaymentModal, BidModal } from "./workspace/JobModals";
import { DispatchModal, ProofModal } from "./workspace/Dispatch";
import { NeedHelpPill, MyTickets } from "./workspace/Support";
import { BuyerSamplePacks } from "./workspace/SamplePacks";

const FIN_VIEWS = ["settlements", "invoices", "onboarding"];

const FinancialsTabs = ({ items, view, onSelect }) => (
  <div className="fin-tabs-wrap"><div className="fin-tabs" role="tablist" data-testid="financials-subtabs">
    {items.map(n => (
      <button key={n.id} role="tab" aria-selected={view === n.id} data-testid={`fin-tab-${n.id}`}
        className={view === n.id ? "fin-tab active" : "fin-tab"} onClick={() => onSelect(n)}>
        <n.icon size={14}/>{n.label}
      </button>
    ))}
  </div></div>
);

export default function Workspace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "buyer";
  const supplierId = roleToSupplierId(user);
  const buyerId = user?.role === "buyer" ? user.id : "buyer-1";
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("section") || "overview");
  const [finOpen, setFinOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [selected, setSelected] = useState(null);
  const [bids, setBids] = useState([]);
  const [modal, setModal] = useState(null);
  const [selectedBidId, setSelectedBidId] = useState(null);
  const [kycPending, setKycPending] = useState(0);
  const [adminInitialTab, setAdminInitialTab] = useState("dashboard");
  const [postHireFocusBriefId, setPostHireFocusBriefId] = useState(null);
  const kycAutoOpened = useRef(false);

  const load = async () => {
    const q = supplierId ? `role=${role}&supplier_id=${supplierId}` : `role=${role}`;
    const requests = [
      axios.get(`${API}/jobs?${q}`),
      axios.get(`${API}/briefs`),
      axios.get(`${API}/metrics`),
    ];
    // Agencies get an extra scoped-metrics call so the Overview cards reflect
    // their creative workload (active briefs, awaiting feedback, ready for
    // print, earnings this month) instead of the global procurement counters.
    if (role === "agency") {
      requests.push(axios.get(`${API}/agency/metrics`).catch(() => ({ data: {} })));
    }
    // Admins see platform-health KPIs (pending review, KYC pending, active
    // disputes, GMV this month) instead of the buyer-flavored counters.
    if (role === "admin") {
      requests.push(axios.get(`${API}/admin/overview-metrics`).catch(() => ({ data: {} })));
    }
    const [j, b, m, extra] = await Promise.all(requests);
    setJobs(j.data);
    setBriefs(b.data);
    setMetrics((role === "agency" || role === "admin") ? { ...m.data, ...(extra?.data || {}) } : m.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, [role, supplierId]);

  // Admin only: fetch pending KYC count every mount & every 60s so the sidebar
  // badge stays fresh. On the first fetch, if there are pending items and we
  // haven't yet auto-opened, snap the admin straight into the KYC queue.
  useEffect(() => {
    if (role !== "admin") return undefined;
    if (["tab", "brief", "job"].some(k => searchParams.get(k))) kycAutoOpened.current = true;
    let cancelled = false;
    const fetchKyc = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/dashboard`);
        if (cancelled) return;
        const pending = data?.kyc?.pending || 0;
        setKycPending(pending);
        if (pending > 0 && !kycAutoOpened.current) {
          kycAutoOpened.current = true;
          setAdminInitialTab("kyc");
          setView("admin-controls");
        }
      } catch {}
    };
    fetchKyc();
    const t = setInterval(fetchKyc, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [role]);

  const pendingJobs = jobs.filter(j => j.status === "pending_review");
  const openJob = async (job) => {
    setSelected(job);
    setSelectedBidId(null);
    if (job.category) {
      const q = supplierId ? `role=${role}&supplier_id=${supplierId}` : `role=${role}`;
      const { data } = await axios.get(`${API}/jobs/${job.id}/bids?${q}`);
      setBids(data);
    }
  };

  // Deep-link consumer — reads ?tab=jobs&job=job-xxx (etc.) from the URL
  // (produced by Inbox items). Waits until jobs/briefs have loaded so we can
  // snap the drawer open to the right record. Params are cleared after
  // consumption so a plain refresh doesn't re-open the drawer.
  const deepLinkTab = searchParams.get("tab");
  const deepLinkJob = searchParams.get("job");
  const deepLinkBrief = searchParams.get("brief");
  useEffect(() => {
    if (!deepLinkTab && !deepLinkJob && !deepLinkBrief) return;
    // Map requested tab → actual view id (tolerates aliases)
    const tabAlias = {
      jobs: "jobs",
      briefs: role === "admin" ? "design-services" : "briefs",
      "design-services": "design-services",
      design_services: role === "buyer" ? "briefs" : "design-services",
      settlements: "settlements",
      invoices: "invoices",
      "job-board": "job-board",
      "partner-orders": "partner-orders",
      capabilities: "capabilities",
      onboarding: "onboarding",
      payouts: "onboarding",
      ewaybills: "ewaybills",
      "zoho-sync": "zoho-sync",
      "sub-rfqs": "sub-rfqs",
      support: "support",
      "sample-packs": "sample-packs",
      profile: role === "buyer" ? "plans" : "onboarding",
      plans: "plans",
      directory: "directory",
      "admin-controls": "admin-controls",
      "bid-calculator": "bid-calculator",
      "print-tools": "print-tools",
    };
    const partnerAlias = { jobs: "job-board", milestones: "partner-orders", "sub-rfqs": "job-board" };
    const target = deepLinkTab ? ((isPartner && partnerAlias[deepLinkTab]) || tabAlias[deepLinkTab]) : null;
    if (target && nav.find(n => n.id === target)) {
      setView(target);
    } else if (role === "admin" && ["disputes", "admin-chats", "conversations", "kyc", "admin-kyc"].includes(deepLinkTab)) {
      setAdminInitialTab(deepLinkTab === "disputes" ? "disputes" : deepLinkTab.includes("kyc") ? "kyc" : "conversations");
      setView("admin-controls");
    }
    if (deepLinkJob) {
      const j = jobs.find(x => x.id === deepLinkJob);
      if (j) {
        openJob(j);
        setSearchParams({ section: target && nav.find(n => n.id === target) ? target : view, open: j.id }, { replace: true });
      }
      // if not found yet, leave params in place — effect re-runs when jobs loads
      return;
    }
    if (deepLinkBrief) {
      if (role === "buyer" || role === "agency" || role === "admin") {
        setView(role === "buyer" ? "briefs" : "design-services");
        setPostHireFocusBriefId(deepLinkBrief);
        setSearchParams({}, { replace: true });
        return;
      }
      const b = briefs.find(x => x.id === deepLinkBrief);
      if (b) {
        setSelected(b);
        setSearchParams({}, { replace: true });
      }
      return;
    }
    // Tab-only link — clear params immediately.
    if (deepLinkTab) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTab, deepLinkJob, deepLinkBrief, jobs, briefs, role]);

  // Strict RBAC: only surface nav items relevant to this user's role
  const nav = [
    { id: "overview", label: "Overview", icon: LayoutDashboard, roles: ["admin", "buyer", "printer", "agency", "material_supplier", "finishing_partner"] },
    { id: "job-board", label: "Job Board", icon: ClipboardCheck, roles: ["material_supplier", "finishing_partner"] },
    { id: "partner-orders", label: "My Orders", icon: Milestone, roles: ["material_supplier", "finishing_partner"] },
    { id: "jobs", label: role === "admin" ? "Review queue" : (role === "printer" ? "Job Board" : "My jobs"), icon: ClipboardCheck, roles: ["admin", "buyer", "printer"] },
    { id: "briefs", label: role === "agency" ? "Design projects" : "Design briefs", icon: Sparkles, roles: ["buyer", "agency"] },
    // "Design services" is the open-briefs-to-quote inbox for agencies and the admin review view.
    // Buyers see the unified Active/Awarded/Completed layout on "Design briefs" — no need for a duplicate nav.
    { id: "design-services", label: role === "agency" ? "Design inbox" : "Design", icon: Sparkles, roles: ["admin", "agency"] },
    { id: "directory", label: "Supplier network", icon: Users, roles: ["admin", "buyer"] },
    { id: "milestones", label: "My Orders", icon: Milestone, roles: ["printer"] },
    { id: "admin-controls", label: "Admin controls", icon: LayoutDashboard, roles: ["admin"], badge: kycPending },
    { id: "sourcing", label: "Materials & Finishing", icon: Package, roles: ["printer"] },
    { id: "analytics", label: "Bid analytics", icon: BarChart3, roles: ["printer"] },
    { id: "capabilities", label: "Capabilities", icon: Sparkles, roles: ["printer", "material_supplier", "finishing_partner"] },
    { id: "studio", label: "Studio profile", icon: Sparkles, roles: ["agency"] },
    { id: "settlements", label: "Settlements", icon: FileText, roles: ["printer", "agency"] },
    { id: "sub-rfqs", label: "Sub-RFQs", icon: Package, roles: ["admin"] },
    { id: "ewaybills", label: "E-way bills", icon: FileWarning, roles: ["admin"] },
    { id: "zoho-sync", label: "Zoho sync", icon: RefreshCw, roles: ["admin"] },
    { id: "invoices", label: "Invoices", icon: FileText, roles: ["buyer", "printer", "agency", "admin", "material_supplier", "finishing_partner"] },
    { id: "onboarding", label: "Payouts", icon: CreditCard, roles: ["printer", "agency", "material_supplier", "finishing_partner"] },
    { id: "design-library", label: "Design library", icon: PackageCheck, roles: ["buyer", "agency"] },
    { id: "sample-packs", label: "Sample packs", icon: Package, roles: ["buyer"] },
    { id: "support", label: "My tickets", icon: MessageSquare, roles: ["buyer", "printer", "agency", "material_supplier", "finishing_partner"] },
    { id: "bid-calculator", label: "Bid Calculator & Fee Guide", icon: Calculator, roles: ["printer", "agency", "material_supplier", "finishing_partner"] },
    { id: "print-tools", label: "Print tools", icon: Ruler, roles: ["buyer", "printer", "agency", "admin", "material_supplier", "finishing_partner"] },
    { id: "plans", label: "Plans & Billing", icon: CreditCard, roles: ["buyer", "printer", "agency", "material_supplier", "finishing_partner"] },
  ].filter(n => n.roles.includes(role));

  // If someone lands on a view their role can't access, snap back to overview
  useEffect(() => {
    if (!nav.find(n => n.id === view)) setView("overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const canAccess = (v) => nav.some(n => n.id === v);
  const isPartner = ["material_supplier", "finishing_partner"].includes(role);
  const finGrouped = role !== "admin";
  const finItems = finGrouped ? nav.filter(n => FIN_VIEWS.includes(n.id)) : [];
  const inFin = finItems.some(n => n.id === view);
  useEffect(() => { if (inFin) setFinOpen(true); }, [inFin]);
  useEffect(() => { if (role === "admin" && view === "briefs") setView("design-services"); }, [role, view]);
  const goView = (n) => {
    setSelected(null);
    kycAutoOpened.current = true;
    if (n.id === "sourcing") { navigate("/printer/materials"); return; }
    if (n.id === "admin-kyc") {
      setAdminInitialTab("kyc");
      setView("admin-controls");
    } else {
      if (n.id === "admin-controls") setAdminInitialTab("dashboard");
      setView(n.id);
    }
  };
  // Browser history: each section (and an open job/brief) gets its own URL so Back stays inside the workspace.
  const location = useLocation();
  const histInit = useRef(false);
  useEffect(() => {
    if (histInit.current) return;
    histInit.current = true;
    const p = new URLSearchParams(location.search);
    if (["tab", "job", "brief"].some(k => p.get(k))) return;
    const sec = p.get("section") || view;
    const extra = p.get("tool") ? `&tool=${encodeURIComponent(p.get("tool"))}` : "";
    navigate({ search: `?section=${sec}&root=1${extra}` }, { replace: true });
    navigate({ search: `?section=${sec}${extra}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("root")) { toast("Press back again to leave your workspace", { id: "leave-hint", duration: 2500 }); }
    const sec = p.get("section");
    if (sec && sec !== view && nav.find(n => n.id === sec)) { setSelected(null); setView(sec); }
    const open = p.get("open");
    if (!open && selected) setSelected(null);
    if (open && !selected) {
      const j = jobs.find(x => x.id === open);
      if (j) openJob(j);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, jobs]);
  useEffect(() => {
    if (!histInit.current) return;
    const p = new URLSearchParams(location.search);
    if (["tab", "job", "brief"].some(k => p.get(k))) return;
    const curSec = p.get("section"), curOpen = p.get("open");
    const wantOpen = selected?.id && jobs.some(j => j.id === selected.id) ? selected.id : null;
    if (curSec === view && curOpen === wantOpen && !p.get("root")) return;
    if (curSec === view && curOpen && !wantOpen) { navigate(-1); return; }
    const next = new URLSearchParams({ section: view });
    if (wantOpen) next.set("open", wantOpen);
    navigate({ search: `?${next}` }, { replace: !curSec || (curSec === view && curOpen === wantOpen) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selected, location.search === "" || location.search === "?"]);

  const navBtn = (n, sub) => (
    <button
      data-testid={`nav-${n.id}`}
      className={`${view === n.id ? "nav-item active" : "nav-item"}${sub ? " nav-sub" : ""}`}
      onClick={() => goView(n)}
      key={n.id}
    >
      <n.icon size={sub ? 15 : 17}/>{n.label}
      {n.id === "jobs" && role === "admin" && pendingJobs.length > 0 && <em>{pendingJobs.length}</em>}
      {n.id === "admin-controls" && n.badge > 0 && <em data-testid="nav-kyc-badge" title="Pending KYC">{n.badge}</em>}
    </button>
  );

  return (
    <div className="shell">
      <aside className="sidebar">
        <Link to="/" className="brand" data-testid="workspace-brand-home">
          <img src="/logo.png" alt="GoPrintPack" className="brand-logo"/>
        </Link>
        <div className="workspace-label">WORKSPACE</div>
        <nav>
          {nav.map(n => {
            if (!finItems.includes(n)) return navBtn(n);
            if (n !== finItems[0]) return null;
            return (
              <div key="financials" data-testid="nav-group-financials">
                <button data-testid="nav-financials" className={inFin && !finOpen ? "nav-item active" : "nav-item"} aria-expanded={finOpen}
                  onClick={() => { setFinOpen(o => !o); if (!inFin) goView(finItems[0]); }}>
                  <Wallet size={17}/>Financials
                  <ChevronDown size={14} style={{ marginLeft: "auto", transition: "transform .2s", transform: finOpen ? "rotate(180deg)" : "none" }}/>
                </button>
                {finOpen && <div className="nav-sub-group">{finItems.map(f => navBtn(f, true))}</div>}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <div className="security">
            <ShieldCheck size={16}/>
            <div><b>Verified network</b><span>{metrics.network_size || 0} active suppliers</span></div>
          </div>
          <button data-testid="sidebar-settings" className="nav-item"><BarChart3 size={17}/>Reports</button>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <div>
            <div className="crumb">Workspace / <b>{role === "admin" ? "Admin review" : "Operations"}</b></div>
            <h1>{view === "overview" ? `Welcome, ${user?.name?.split(" ")[0] || ""}` : inFin ? "Financials" : nav.find(n => n.id === view)?.label}</h1>
          </div>
          <div className="top-actions">
            <GuideButton role={role} user={user} setView={(v) => { setSelected(null); setView(v); }}/>
            <InboxBell/>
            <div className="user-chip" data-testid="user-chip">
              <span>{user?.role?.toUpperCase()}</span>
              <b>{user?.name}</b>
            </div>
            <button data-testid="logout-button" className="secondary-btn" onClick={logout}>Sign out</button>
            <div className="avatar">{user?.name?.[0]?.toUpperCase() || "?"}</div>
          </div>
        </header>
        <PhoneVerifyBanner/>
        {inFin && finItems.length > 1 && <FinancialsTabs items={finItems} view={view} onSelect={goView}/>}
        {view === "overview" && isPartner && <PartnerOverview user={user} setView={setView} openJob={(id) => { const j = jobs.find(x => x.id === id); if (j) openJob(j); }}/>}
        {view === "job-board" && isPartner && <PartnerJobBoard user={user} jobs={jobs} openJob={openJob}/>}
        {view === "partner-orders" && isPartner && <PartnerOrders user={user} jobs={jobs} openJob={openJob} milestones={<SupplierMilestones supplierId={supplierId} jobs={jobs}/>}/>}
        {view === "capabilities" && isPartner && <PartnerCapabilities role={role}/>}
        {view === "overview" && !isPartner && <Overview role={role} metrics={metrics} jobs={jobs} briefs={briefs} setView={setView} openJob={openJob} setModal={setModal} focusBrief={(id) => { setPostHireFocusBriefId(id); setView("briefs"); }}/>}
        {view === "jobs" && canAccess("jobs") && <Jobs role={role} jobs={jobs} openJob={openJob} setModal={setModal}/>}
        {/* Buyers get the unified 3-tab layout (Active · Awarded · Completed) on Design briefs itself,
            so we route "briefs" to DesignServices for them. Admin + agency keep the kanban (design queue). */}
        {view === "briefs" && canAccess("briefs") && role === "buyer" && <DesignServices role={role} userId={user?.id} user={user} focusBriefId={postHireFocusBriefId} onFocusHandled={() => setPostHireFocusBriefId(null)}/>}
        {view === "briefs" && canAccess("briefs") && role !== "buyer" && <DesignProjects role={role} supplierId={supplierId} setModal={setModal} openJob={openJob}/>}
        {view === "design-services" && role === "admin" && <AdminDesign userId={user?.id} user={user} focusBriefId={postHireFocusBriefId} onFocusHandled={() => setPostHireFocusBriefId(null)}/>}
        {view === "design-services" && canAccess("design-services") && role !== "admin" && <DesignServices role={role} userId={user?.id} user={user} focusBriefId={postHireFocusBriefId} onFocusHandled={() => setPostHireFocusBriefId(null)}/>}
        {view === "directory" && role === "admin" && <AdminSupplierNetwork role={role} onHireSuccess={(brief) => { setPostHireFocusBriefId(brief.id); setView("design-services"); }}/>}
        {view === "directory" && canAccess("directory") && role !== "admin" && <Directory role={role} onHireSuccess={(brief) => { setPostHireFocusBriefId(brief.id); setView(role === "buyer" ? "briefs" : "design-services"); }}/>}
        {view === "analytics" && canAccess("analytics") && <SupplierAnalytics supplierId={supplierId}/>}
        {view === "capabilities" && canAccess("capabilities") && !isPartner && <><PrinterCapabilitiesPanel supplierId={supplierId}/><DeliveryRateCard/></>}
        {view === "studio" && canAccess("studio") && <StudioProfile supplierId={supplierId}/>}
        {view === "onboarding" && canAccess("onboarding") && <PayoutsRouter supplierId={supplierId}/>}
        {view === "settlements" && canAccess("settlements") && <SupplierSettlements supplierId={supplierId}/>}
        {view === "ewaybills" && canAccess("ewaybills") && <EwayBillQueue/>}
        {view === "zoho-sync" && canAccess("zoho-sync") && <ZohoSyncReport/>}
        {view === "sub-rfqs" && canAccess("sub-rfqs") && <AdminSubRfqs/>}
        {view === "invoices" && canAccess("invoices") && <UserInvoices userId={user?.id} role={role}/>}
        {view === "design-library" && canAccess("design-library") && <DesignLibrary role={role}/>}
        {view === "sample-packs" && canAccess("sample-packs") && <BuyerSamplePacks user={user}/>}
        {view === "support" && canAccess("support") && <MyTickets user={user}/>}
        {view === "milestones" && canAccess("milestones") && <SupplierMilestones supplierId={supplierId} jobs={jobs}/>}
        {view === "admin-controls" && canAccess("admin-controls") && <AdminControls initialTab={adminInitialTab}/>}
        {view === "bid-calculator" && canAccess("bid-calculator") && <BidCalculatorPage/>}
        {view === "print-tools" && canAccess("print-tools") && <PrintTools/>}
        {view === "plans" && canAccess("plans") && <PlansPage role={role} userId={supplierId || buyerId}/>}
      </main>
      {selected && (
        <JobDrawer
          job={selected}
          bids={bids}
          role={isPartner ? "printer" : role}
          supplierId={supplierId}
          close={() => setSelected(null)}
          refresh={load}
          setModal={setModal}
          selectedBidId={selectedBidId}
          setSelectedBidId={setSelectedBidId}
          reloadBids={async () => {
            const q = supplierId ? `role=${role}&supplier_id=${supplierId}` : `role=${role}`;
            const { data } = await axios.get(`${API}/jobs/${selected.id}/bids?${q}`);
            setBids(data);
          }}
        />
      )}
      {modal === "job" && <JobModal close={() => setModal(null)} refresh={load}/>}
      {modal === "brief" && <DesignJobModal close={() => setModal(null)} refresh={load}/>}
      {modal === "payment" && <PaymentModal
        job={selected}
        bid={bids.find(b => b.id === selectedBidId) || bids[0]}
        close={() => setModal(null)}
        refresh={async () => { await load(); }}
        onAwarded={() => {
          // Buyer asked for the award to collapse the drawer immediately so
          // they land back on the job list where the row now shows "awarded".
          // We stage the collapse so the success toast is seen before the
          // drawer and modal both tear down (keeps motion feeling intentional).
          setModal(null);
          setSelected(null);
          setBids([]);
          setSelectedBidId(null);
        }}
      />}
      {modal === "bid" && <BidModal job={selected} supplierId={supplierId} close={() => setModal(null)} refresh={async () => { await load(); }} onSuccess={() => { setSelected(null); setBids([]); setSelectedBidId(null); }}/>}
      {modal === "bid-edit" && bids[0] && <BidModal existing={bids[0]} job={selected} supplierId={supplierId} close={() => setModal(null)} refresh={async () => { await load(); }} onSuccess={() => { setSelected(null); setBids([]); setSelectedBidId(null); }}/>}
      {modal === "dispatch" && <DispatchModal job={selected} close={() => setModal(null)} refresh={load}/>}
      {modal === "proof" && <ProofModal job={selected} close={() => setModal(null)} refresh={load}/>}
      {modal === "dispute" && <DisputeModal job={selected} role={role} supplierId={supplierId} close={() => setModal(null)} refresh={load}/>}
      {modal === "rate" && <RateDeliveryModal job={selected} close={() => setModal(null)} refresh={async () => { await load(); }}/>}
      {modal === "resolveDispute" && <ResolveDisputeModal dispute={selected} close={() => setModal(null)} refresh={load}/>}
      <NeedHelpPill user={user} role={role}/>
    </div>
  );
}
